import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SignalHigh, SignalLow, LogOut, Play as PlayIcon } from "lucide-react";
import { GameState, Player } from "@/types/poker";

/**
 * Simplified props for the top bar. It receives all necessary data and callbacks
 * from its parent component (`PokerTable`) and is purely presentational.
 */
interface PokerTopBarProps {
  state: GameState;
  you?: Player;
  connected: boolean;
  onReady: () => void;
  onLeave: () => void;
}

export const PokerTopBar = ({ state, you, connected, onReady, onLeave }: PokerTopBarProps) => {
  const { t } = useTranslation();
  const totalPot = state.pot ?? 0;

  const readyCount = useMemo(() => {
    return state.players.filter((p) => p.isReady).length;
  }, [state.players]);

  const playerCount = state.players.length;
  const isReady = you?.isReady;

  return (
    <Card className="border border-white/10 bg-gradient-to-br from-background to-primary/5 p-4 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {/* Left Section: Room Info & Connection Status */}
        <div className="flex items-center gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{t('poker_room')}</p>
            <p className="text-xl font-semibold text-primary">{state.room}</p>
          </div>
          <Badge
            className={cn(
              "flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold",
              connected
                ? "border border-emerald-400/40 bg-emerald-500/20 text-emerald-200"
                : "border border-destructive/40 bg-destructive/20 text-destructive-foreground",
            )}
          >
            {connected ? <SignalHigh className="h-3.5 w-3.5" /> : <SignalLow className="h-3.5 w-3.5" />}
            {connected ? t('poker_live') : t('poker_offline')}
          </Badge>
        </div>

        {/* Center Section: Game Info (Pot & Phase) */}
        <div className="flex items-center gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{t('poker_total_pot')}</p>
            <p className="text-xl font-semibold text-secondary">{totalPot}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('poker_phase')}</p>
            <p className="text-xl font-semibold capitalize">{state.phase}</p>
          </div>
        </div>

        {/* Right Section: Main Actions */}
        <div className="flex items-center gap-2">
          {state.phase === "idle" && (
            <Button
              size="sm"
              onClick={onReady}
              variant={isReady ? "default" : "secondary"}
              className={cn(
                "shadow-glow transition-all",
                isReady
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/90",
              )}
            >
              <PlayIcon className="mr-2 h-4 w-4" />
              {isReady ? t('poker_ready') : t('poker_im_ready')}
              <span className="ml-2 text-xs opacity-80">
                ({readyCount}/{playerCount})
              </span>
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onLeave} className="h-8 w-8 rounded-full" title="Leave Table">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
